/**
 * Story 24.3 — where focus lands when a Sheet, Dialog or AlertDialog opens.
 *
 * The first tabbable control inside the content, never the always-present
 * close X (which is the content's first child in DOM order, so it is skipped
 * by its `data-slot`); if there is none (a read-only sheet), the Title, which
 * always carries `tabIndex={-1}`. Controls outside the Tab order are skipped:
 * a negative `tabIndex`, and an unchecked native radio whose group has a
 * checked member (Tab reaches only the checked one).
 * A caller's own `onOpenAutoFocus` runs first, and if it calls
 * `preventDefault()` it wins.
 */

const TABBABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Close-X controls carry a `data-slot` ending in `-close-x`. */
const CLOSE_X = '[data-slot$="-close-x"]';

/** An unchecked native radio whose group (same `name` and form) has a checked member. */
function isIdleRadio(el: HTMLElement, candidates: Iterable<HTMLElement>): boolean {
  if (!(el instanceof HTMLInputElement) || el.type !== 'radio' || el.checked || !el.name) return false;
  for (const other of candidates) {
    if (
      other instanceof HTMLInputElement &&
      other.type === 'radio' &&
      other.checked &&
      other.name === el.name &&
      other.form === el.form
    ) {
      return true;
    }
  }
  return false;
}

export function firstTabbable(container: HTMLElement): HTMLElement | null {
  const candidates = container.querySelectorAll<HTMLElement>(TABBABLE);
  for (const el of candidates) {
    if (el.matches(CLOSE_X) || el.closest(CLOSE_X)) continue;
    if (el.closest('[hidden], [inert]')) continue;
    if (el.getAttribute('aria-hidden') === 'true') continue;
    // A roving-tabindex group's idle members (`tabindex="-1"` set as a
    // property or attribute) are not in the Tab order.
    if (el.tabIndex < 0) continue;
    // Nor are a native radio group's unchecked members once one is checked:
    // focus the checked one, so an arrow key moves from the real selection.
    if (isIdleRadio(el, candidates)) continue;
    return el;
  }
  return null;
}

/**
 * Build an `onOpenAutoFocus` handler for a layer's content. `titleSelector`
 * finds the layer's Title (the fallback target). `preferSelector`, when
 * given, names a control that wins over the first tabbable — the
 * AlertDialog passes its Cancel, which is Radix's own default for an
 * alertdialog and which this handler would otherwise pre-empt.
 */
export function layerOpenAutoFocus(
  titleSelector: string,
  callerHandler?: (event: Event) => void,
  preferSelector?: string,
): (event: Event) => void {
  return (event: Event) => {
    callerHandler?.(event);
    if (event.defaultPrevented) return;
    const content = event.target;
    if (!(content instanceof HTMLElement)) return;
    event.preventDefault();
    const candidate = preferSelector ? content.querySelector<HTMLElement>(preferSelector) : null;
    // A disabled preferred control cannot take focus: fall through.
    const preferred = candidate && !candidate.matches(':disabled') ? candidate : null;
    const target =
      preferred ?? firstTabbable(content) ?? content.querySelector<HTMLElement>(titleSelector);
    (target ?? content).focus({ preventScroll: true });
  };
}
